import { interruptionDecision } from "./InterruptionPolicy";
import { PresentationQueue } from "./PresentationQueue";
import type { PresentationHistory, PresentationRequest, SceneState } from "./types";
import { timing } from "./timing";

export class SceneOrchestrator {
  readonly queue = new PresentationQueue();
  current: SceneState | null = null;
  history: PresentationHistory[] = [];

  submit(request: PresentationRequest, now = Date.now()): boolean {
    const currentKey = this.current?.request.dedupeKey ?? this.current?.request.id;
    const incomingKey = request.dedupeKey ?? request.id;
    if (currentKey === incomingKey && this.current) {
      this.current = { ...this.current, request };
      return false;
    }
    const decision = interruptionDecision(this.current, request, now);
    if (decision === "discard") return false;
    if (decision === "queue") return this.queue.enqueue(request, now);
    this.activate(request, now);
    return true;
  }

  tick(ambient: PresentationRequest, now = Date.now()): SceneState {
    if (this.current?.request.kind === "ambient") {
      if (this.current.request.id === ambient.id) this.current = { ...this.current, request: ambient };
      else this.activate(ambient, now);
    }
    if (!this.current || (this.current.endsAt !== undefined && this.current.endsAt <= now)) {
      const next = this.queue.next(now) ?? ambient;
      this.activate(next, now);
    }
    return this.current!;
  }

  private activate(request: PresentationRequest, now: number): void {
    if (this.current?.request.resumable && request.priority > this.current.request.priority) this.queue.enqueue(this.current.request, now);
    this.current = { request, startedAt: now, endsAt: request.durationMs ? now + request.durationMs : undefined };
    const key = request.dedupeKey ?? request.id;
    this.history = [...this.history.filter(item => item.key !== key), { key, shownAt: now }].slice(-timing.historyLimit);
  }
}
